'use client';
import React, { useRef } from 'react';

interface Props {
  children: React.ReactNode;
  className?: string;
  strength?: number;
}

const canAnimate = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: fine) and (prefers-reduced-motion: no-preference)').matches;

/**
 * Subtle magnetic pull toward the cursor. Inert on touch devices and under reduced motion.
 * Timing mirrors animationConfig (fast in, smooth settle).
 */
export default function MagneticButton({ children, className = '', strength = 0.2 }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !canAnimate()) return;
    const rect = el.getBoundingClientRect();
    const dx = (e.clientX - (rect.left + rect.width / 2)) * strength;
    const dy = (e.clientY - (rect.top + rect.height / 2)) * strength;
    el.style.transform = `translate(${dx}px, ${dy}px)`;
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)';
    el.style.transform = 'translate(0px, 0px)';
  };

  const handleEnter = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)';
  };

  return (
    <div
      ref={ref}
      className={`inline-block ${className}`}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onMouseEnter={handleEnter}
    >
      {children}
    </div>
  );
}
